import React, { useEffect, useState } from 'react';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { styled } from '@uhg-abyss/web/tools/styled';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { useSelector } from 'react-redux';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { config } from '@uhg-abyss/web/tools/config';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { ErrorComponent } from '@src/common/Error';
import { useCalendarApi } from '@src/hooks/useCalendarApi';
import { LagoonContext } from '@src/AppContext';

const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});

const Title = styled('h2', {
  color: 'rgb(0, 38, 119)',
  fontSize: '25.63px !important',
  fontWeight: '700',
  minHeight: '32px',
  lineHeight: '32px',
  marginBottom: '5px',
});

const FlexDisplay = styled('div', {
  width: '100%',
  display: 'flex',
  gap: '20px 4%',
  alignItems: 'baseline',
  flexWrap: 'wrap',

  '@screen < $sm': {
    gap: '20px 0',
  },
});

const FlexDisplayRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  gap: '20px 4%',
  flexWrap: 'wrap',
  flexDirection: 'row',

  '@screen < $sm': {
    gap: '20px 0',
  },
});

const FlexItem = styled('div', {
  flexBasis: '55%',
  '@screen < $sm': {
    flexBasis: '100%',
  },
});

export const ScheduleMeeting = () => {
  const form = useForm();
  const [calendarDateApi, setCalendarDate] = useCalendarApi();
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const holidayDateApi = useSelector((state) => {
    return state.GET_CALENDAR_HOLIDAY.data;
  });
  const { nextWorkingDay, lastAllowedWorkingDay } = holidayDateApi || {};
  const [consultantOptions, setConsultantOptions] = useState([]);
  const [preferredConsultant, setPreferredConsultant] = useState('');
  const lagoonContext = React.useContext(LagoonContext);
  const lagoonConfigs = lagoonContext?.getTable('configs');
  const [isLoading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dentalConsultantIds = lagoonConfigs?.getRow('DENTAL_CON_IDS')
    ? lagoonConfigs?.getRow('DENTAL_CON_IDS')?.value
    : config('DENTAL_CON_IDS');

  const validateDate = (date) => {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return dateRegex.test(date);
  };

  const callCalendarAPI = (date) => {
    form.setValue('meeting.consultant', '');
    form.setValue('meeting.preferredMeetingTime', '');
    if (validateDate(date)) {
      setCalendarDate({ userInfo, date });
    }
  };

  useEffect(() => {
    // if a new consultant is selected, clear the meeting times for the previous one
    form.setValue('preferredMeetingTime', '');
  }, [preferredConsultant]);

  const unavailableCalendarDates = () => {
    if (nextWorkingDay) {
      return new Date(nextWorkingDay);
    }
    // if holidayDateApi is failing, use the next day as next available date
    return new Date();
  };

  const apiCalendarMaximumDate = () => {
    if (lastAllowedWorkingDay) {
      return new Date(lastAllowedWorkingDay);
    }
    // fallback scenario
    const maximumDate = new Date();
    maximumDate.setDate(maximumDate.getDate() + 30);

    return maximumDate;
  };

  const isHoliday = (excludeDate) => {
    return holidayDateApi?.holidays?.some((holiday) => {
      const excludeDateConfigured = new Date(excludeDate).toLocaleDateString(
        'en-US',
        {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }
      );
      return holiday === excludeDateConfigured;
    });
  };
  const consultantData = () => {
    const consultantsList = [];

    let availableConsultant = calendarDateApi?.data?.consultants || [];
    const selectedAPIs = form.getValues('orgInfoPartTwo.selectedAPIs') || [];
    const isDentalApiSelected =
      Array.isArray(selectedAPIs) && selectedAPIs.includes('Dental API');
    // If Dental API is selected, filter consultants to only include 'Staci'
    const dentalConIdsArray = dentalConsultantIds?.split(',').map((id) => {
      return parseInt(id.trim(), 10);
    }); // Convert string to array of integers with radix
    if (isDentalApiSelected) {
      setPreferredConsultant('');
      availableConsultant = availableConsultant.filter((consultant) => {
        return dentalConIdsArray?.some((id) => {
          return consultant?.consultantId === id;
        });
      });
    }

    availableConsultant.forEach((element) => {
      consultantsList.push({
        label: `${element?.firstName} ${element?.lastNameInitials}`,
        value: element.consultantId,
      });
    });
    setConsultantOptions(consultantsList);
  };
  useEffect(() => {
    consultantData();
    if (calendarDateApi.loading) {
      setLoading(true);
      setIsOpen(true);
    } else {
      setLoading(false);
      setIsOpen(false);
    }
  }, [calendarDateApi]);
  // eslint-disable-next-line no-unused-vars
  const consultantData12 = () => {
    const consultantsList = [];

    const availableConsultant = calendarDateApi?.data?.consultants || [];
    availableConsultant.forEach((element) => {
      consultantsList.push({
        label: `${element?.firstName} ${element?.lastNameInitials}`,
        value: element.consultantId,
      });
    });
    return consultantsList;
  };

  const consultantTime = () => {
    const userSelectedConsultant = form.getValues('meeting.consultant');
    const consultantAvailableTimes = calendarDateApi?.data?.consultants?.find(
      (consultant) => {
        return consultant?.consultantId === userSelectedConsultant;
      }
    )?.availableTimes;

    const consultantsAvailableTimeOptions = consultantAvailableTimes?.map(
      (time) => {
        return {
          label: time,
          value: time,
        };
      }
    );

    return consultantsAvailableTimeOptions;
  };

  return (
    <Wrapper>
      <LoadingOverlay
        loadingTitle="Please Wait"
        loadingMessage="Retrieving Consultant Availability."
        statusTitle="Status update"
        statusMessage="Sample message..."
        isDismissable
        ariaLoadingLabel="Example loading aria label"
        isLoading={isLoading}
        isOpen={isOpen}
        // onClose={handleClose}
      >
        <Title style={{ textAlign: 'left' }}>
          Schedule a meeting with an API Consultant to talk next steps and get
          expert help moving forward.
        </Title>
        <FlexDisplay>
          <FlexItem>
            <DateInput
              id="preferredMeetingDate"
              onChange={callCalendarAPI}
              model="meeting.preferredMeetingDate"
              minimumDate={unavailableCalendarDates()}
              maximumDate={apiCalendarMaximumDate()}
              excludeDate={(excludeDate) => {
                return (
                  excludeDate.getDay() === 0 ||
                  excludeDate.getDay() === 6 ||
                  isHoliday(excludeDate)
                );
              }}
              label="Preferred meeting date"
              validators={{ required: true }}
              subText={
                <span
                  style={{
                    fontWeight: '1',
                    color: '#7d7f81',
                    fontSize: '13px',
                  }}
                >
                  Date Format: mm/dd/yyyy
                </span>
              }
              css={{
                'abyss-date-picker-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
            {calendarDateApi?.error ? (
              <div
                style={{
                  textAlign: 'left', // Ensures the error message is left-aligned
                  marginTop: '5px', // Adds spacing between the dropdown and the error message
                }}
              >
                <ErrorComponent>
                  {calendarDateApi?.data?.message}
                </ErrorComponent>
              </div>
            ) : null}
          </FlexItem>

          <FlexDisplayRow>
            <FlexItem style={{ flex: '0 0 55%' }}>
              <SelectInput
                id="consultant"
                label="Preferred consultant"
                model="meeting.consultant"
                isDisabled={
                  !form.getValues('meeting.preferredMeetingDate') ||
                  !calendarDateApi?.data ||
                  !Object.keys(calendarDateApi?.data)?.length
                }
                validators={{ required: true }}
                onChange={setPreferredConsultant}
                options={consultantOptions}
                placeholder="Select a consultant"
                css={{
                  'abyss-select-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
            <FlexItem style={{ flex: '0 0 40%' }}>
              <div
                style={{
                  fontWeight: '1',
                  color: '#7d7f81',
                  fontSize: '13px',
                  textAlign: 'left',
                }}
              >
                If there are no consultants available or you don't see your
                preferred one, please select a different date.
              </div>
            </FlexItem>
          </FlexDisplayRow>
          <FlexItem>
            <SelectInput
              id="preferredMeetingTime"
              label="Preferred meeting time of day"
              model="meeting.preferredMeetingTime"
              isDisabled={
                !form.getValues('meeting.consultant') ||
                !calendarDateApi?.data ||
                !Object.keys(calendarDateApi?.data)?.length
              }
              validators={{ required: true }}
              options={consultantTime()}
              placeholder="Select a meeting time"
              subText={
                <span
                  style={{
                    fontWeight: '1',
                    color: '#7d7f81',
                    fontSize: '13px',
                  }}
                >
                  Eastern Time Zone (ET)
                </span>
              }
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-descriptors-sub-text': {
                  fontSize: '10px',
                },
              }}
            />
          </FlexItem>
        </FlexDisplay>
      </LoadingOverlay>
    </Wrapper>
  );
};
